
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Database, Upload, FileText, Layers, Briefcase, PlusCircle, Trash2, Flag, FlagOff, Pencil, TrainTrack } from 'lucide-react';
import { toast } from "sonner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import IntegrationsDialog from "@/components/datahub/IntegrationsDialog";
import { Skeleton } from "@/components/ui/skeleton";
import LazyLoad from "@/components/LazyLoad";

const DataTabsSkeleton = () => (
  <div className="space-y-4 animate-pulse">
    <Skeleton className="h-10 w-full max-w-md mb-4" />
    <Skeleton className="h-48 w-full" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const DataHub = () => {
  const [editingField, setEditingField] = useState<string | null>(null);
  const [mappedFields, setMappedFields] = useState([
    { original: "cust_id", mapped: "customer_id", source: "ERP", aiMapped: true },
    { original: "prin_amt", mapped: "principal_amount", source: "Core Banking", aiMapped: false },
  ]);
  const [integrationsDialogOpen, setIntegrationsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Optimize with faster initial load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 50); // Quick initial load
    return () => clearTimeout(timer);
  }, []);

  const handleEditMapping = (original: string) => {
    setEditingField(original);
  };

  const handleSaveMapping = (original: string, newMapped: string) => {
    setMappedFields(mappedFields.map(field => 
      field.original === original 
        ? { ...field, mapped: newMapped, aiMapped: false } 
        : field
    ));
    setEditingField(null);
    toast.success("Mapping updated successfully");
  };

  const handleTrainAIAgent = () => {
    toast.success("Starting AI Browser Agent training process");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Data Hub</h1>
          <p className="text-muted-foreground">
            Centralized data management and analytics platform.
          </p>
        </div>
        <div className="flex space-x-3">
          <Button 
            className="bg-rench-green text-black hover:bg-rench-green/90"
            onClick={() => setIntegrationsDialogOpen(true)}
          >
            <Database className="mr-2 h-4 w-4" /> Connect Data Source
          </Button>
          <Button 
            className="bg-blue-500 text-white hover:bg-blue-600"
            onClick={handleTrainAIAgent}
          >
            <TrainTrack className="mr-2 h-4 w-4" /> Train AI Browser Agent
          </Button>
        </div>
      </div>

      {isLoading ? (
        <DataTabsSkeleton />
      ) : (
        <Tabs defaultValue="data-fields" className="space-y-4">
          <TabsList className="bg-muted/50 p-0.5">
            <TabsTrigger value="data-fields" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Data Fields</TabsTrigger>
            <TabsTrigger value="uploads" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Upload Management</TabsTrigger>
          </TabsList>
          
          {/* Data Fields Tab - Moved from Settings */}
          <TabsContent value="data-fields" className="space-y-4">
            <LazyLoad delay={50}>
              <Card className="border-border/40 shadow-sm">
                <CardHeader>
                  <CardTitle>Data Fields Management</CardTitle>
                  <CardDescription>Manage data fields from various sources</CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs defaultValue="raw-fields" className="space-y-4">
                    <TabsList className="bg-muted/30 p-0.5">
                      <TabsTrigger value="raw-fields" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Raw Fields</TabsTrigger>
                      <TabsTrigger value="mapped-fields" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Mapped Fields</TabsTrigger>
                      <TabsTrigger value="processed-fields" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Processed Fields</TabsTrigger>
                      <TabsTrigger value="new-field" className="rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm">Add New Field</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="raw-fields">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Field Name</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Data Type</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {[
                            { name: "customer_id", source: "ERP", type: "String" },
                            { name: "loan_amount", source: "Core Banking", type: "Decimal" },
                            { name: "interest_rate", source: "Core Banking", type: "Decimal" },
                            { name: "disbursement_date", source: "ERP", type: "Date" },
                          ].map((field, i) => (
                            <TableRow key={i}>
                              <TableCell>{field.name}</TableCell>
                              <TableCell>{field.source}</TableCell>
                              <TableCell>{field.type}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TabsContent>

                    <TabsContent value="mapped-fields">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Original Field</TableHead>
                            <TableHead>Mapped Field</TableHead>
                            <TableHead>Source</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mappedFields.map((field, i) => (
                            <TableRow key={i}>
                              <TableCell>{field.original}</TableCell>
                              <TableCell>
                                {editingField === field.original ? (
                                  <Input 
                                    defaultValue={field.mapped} 
                                    className="w-full rench-input" 
                                    onBlur={(e) => handleSaveMapping(field.original, e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter') {
                                        handleSaveMapping(field.original, e.currentTarget.value);
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  field.mapped
                                )}
                              </TableCell>
                              <TableCell>{field.source}</TableCell>
                              <TableCell>
                                {field.aiMapped ? (
                                  <div className="flex items-center">
                                    <Badge variant="outline" className="bg-rench-green/10 text-rench-green border-rench-green/20 flex gap-1.5 items-center">
                                      <Flag className="h-3 w-3" />
                                      <span className="text-xs">AI Mapped</span>
                                    </Badge>
                                  </div>
                                ) : (
                                  <div className="flex items-center">
                                    <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/20 flex gap-1.5 items-center">
                                      <FlagOff className="h-3 w-3" />
                                      <span className="text-xs">Human Review</span>
                                    </Badge>
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex space-x-2">
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleEditMapping(field.original)}
                                    className="hover:bg-rench-green/10 hover:text-rench-green"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => {
                                      setMappedFields(mappedFields.filter(f => f.original !== field.original));
                                      toast.success(`Removed ${field.mapped}`);
                                    }}
                                    className="hover:bg-red-500/10 hover:text-red-500"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TabsContent>

                    <TabsContent value="processed-fields">
                      <div className="rounded-md border overflow-hidden">
                        <table className="min-w-full divide-y divide-border">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-medium">Field Name</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Formula</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Result Type</th>
                              <th className="px-4 py-2 text-left text-sm font-medium">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {[
                              { name: "total_emi", formula: "principal_amount * (interest_rate/12)", type: "Decimal" },
                              { name: "loan_maturity", formula: "disbursement_date + tenure_months", type: "Date" },
                            ].map((field, i) => (
                              <tr key={i}>
                                <td className="px-4 py-2 text-sm">{field.name}</td>
                                <td className="px-4 py-2 text-sm">{field.formula}</td>
                                <td className="px-4 py-2 text-sm">{field.type}</td>
                                <td className="px-4 py-2 text-sm">
                                  <div className="flex space-x-2">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => toast.success(`Editing ${field.name}`)}
                                      className="hover:bg-rench-green/10 hover:text-rench-green"
                                    >
                                      Edit
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      onClick={() => toast.success(`Removing ${field.name}`)}
                                      className="hover:bg-red-500/10 hover:text-red-500"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </TabsContent>

                    <TabsContent value="new-field">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="field-name">Field Name</Label>
                            <Input id="field-name" placeholder="Enter field name" className="rench-input" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="field-type">Data Type</Label>
                            <Select>
                              <SelectTrigger id="field-type" className="rench-input">
                                <SelectValue placeholder="Select data type" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="string">String</SelectItem>
                                <SelectItem value="number">Number</SelectItem>
                                <SelectItem value="decimal">Decimal</SelectItem>
                                <SelectItem value="date">Date</SelectItem>
                                <SelectItem value="boolean">Boolean</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="field-formula">Formula (for processed fields)</Label>
                          <Input id="field-formula" placeholder="e.g., field1 + field2 or leave blank for raw data" className="rench-input" />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="field-description">Description</Label>
                          <Input id="field-description" placeholder="Enter field description" className="rench-input" />
                        </div>
                        
                        <Button onClick={() => toast.success("Field added successfully")} className="bg-rench-green text-black hover:bg-rench-green/90">
                          Add Field
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </LazyLoad>
          </TabsContent>

          {/* Uploads Tab - Moved from Settings */}
          <TabsContent value="uploads" className="space-y-4">
            <LazyLoad delay={100}>
              <Card className="border-border/40 shadow-sm">
                <CardHeader>
                  <CardTitle>Upload Management</CardTitle>
                  <CardDescription>Manage document uploads and data extraction</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Card className="border-border/40 hover:shadow-md transition-all">
                      <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 mr-2 text-rench-green" />
                          <CardTitle className="text-lg">Bank Statements</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">Upload bank statements for data extraction</p>
                        <div className="border-2 border-dashed border-border/70 rounded-lg p-6 flex flex-col items-center justify-center hover:border-rench-green/50 transition-colors">
                          <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                          <p className="text-sm font-medium mb-1">Drag & drop files or click to upload</p>
                          <p className="text-xs text-muted-foreground mb-3">Supports PDF, CSV, XLSX formats</p>
                          <Button size="sm" className="bg-rench-green text-black hover:bg-rench-green/90" onClick={() => toast.success("Upload dialog would open")}>Select Files</Button>
                        </div>
                        <div className="mt-4 space-y-1">
                          <p className="text-sm font-medium">Extracted Data Fields:</p>
                          <div className="text-xs flex flex-wrap gap-1">
                            {["account_number", "opening_balance", "closing_balance", "transactions", "statement_period"].map((field) => (
                              <Badge key={field} variant="outline" className="text-xs bg-muted/50">{field}</Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 hover:shadow-md transition-all">
                      <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                        <div className="flex items-center">
                          <FileText className="h-5 w-5 mr-2 text-rench-green" />
                          <CardTitle className="text-lg">Borrowing Documents</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">Upload borrowing documents for processing</p>
                        <div className="border-2 border-dashed border-border/70 rounded-lg p-6 flex flex-col items-center justify-center hover:border-rench-green/50 transition-colors">
                          <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                          <p className="text-sm font-medium mb-1">Drag & drop files or click to upload</p>
                          <p className="text-xs text-muted-foreground mb-3">Supports PDF, DOCX formats</p>
                          <Button size="sm" className="bg-rench-green text-black hover:bg-rench-green/90" onClick={() => toast.success("Upload dialog would open")}>Select Files</Button>
                        </div>
                        <div className="mt-4 space-y-1">
                          <p className="text-sm font-medium">Extracted Data Fields:</p>
                          <div className="text-xs flex flex-wrap gap-1">
                            {["lender_name", "loan_amount", "interest_rate", "tenure", "disbursement_date", "repayment_schedule"].map((field) => (
                              <Badge key={field} variant="outline" className="text-xs bg-muted/50">{field}</Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 hover:shadow-md transition-all">
                      <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                        <div className="flex items-center">
                          <Layers className="h-5 w-5 mr-2 text-rench-green" />
                          <CardTitle className="text-lg">Asset Book Data</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">Upload asset book data files</p>
                        <div className="border-2 border-dashed border-border/70 rounded-lg p-6 flex flex-col items-center justify-center hover:border-rench-green/50 transition-colors">
                          <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                          <p className="text-sm font-medium mb-1">Drag & drop files or click to upload</p>
                          <p className="text-xs text-muted-foreground mb-3">Supports CSV, XLSX formats</p>
                          <Button size="sm" className="bg-rench-green text-black hover:bg-rench-green/90" onClick={() => toast.success("Upload dialog would open")}>Select Files</Button>
                        </div>
                        <div className="mt-4 space-y-1">
                          <p className="text-sm font-medium">Extracted Data Fields:</p>
                          <div className="text-xs flex flex-wrap gap-1">
                            {["asset_id", "asset_type", "purchase_date", "original_value", "current_value", "depreciation_rate"].map((field) => (
                              <Badge key={field} variant="outline" className="text-xs bg-muted/50">{field}</Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    <Card className="border-border/40 hover:shadow-md transition-all">
                      <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                        <div className="flex items-center">
                          <Briefcase className="h-5 w-5 mr-2 text-rench-green" />
                          <CardTitle className="text-lg">ERP Data</CardTitle>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground mb-4">Upload ERP data exports</p>
                        <div className="border-2 border-dashed border-border/70 rounded-lg p-6 flex flex-col items-center justify-center hover:border-rench-green/50 transition-colors">
                          <Upload className="h-8 w-8 mb-2 text-muted-foreground" />
                          <p className="text-sm font-medium mb-1">Drag & drop files or click to upload</p>
                          <p className="text-xs text-muted-foreground mb-3">Supports CSV, XLSX formats</p>
                          <Button size="sm" className="bg-rench-green text-black hover:bg-rench-green/90" onClick={() => toast.success("Upload dialog would open")}>Select Files</Button>
                        </div>
                        <div className="mt-4 space-y-1">
                          <p className="text-sm font-medium">Extracted Data Fields:</p>
                          <div className="text-xs flex flex-wrap gap-1">
                            {["gl_code", "transaction_date", "amount", "description", "cost_center", "department"].map((field) => (
                              <Badge key={field} variant="outline" className="text-xs bg-muted/50">{field}</Badge>
                            ))}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </CardContent>
              </Card>
            </LazyLoad>
          </TabsContent>
        </Tabs>
      )}

      {/* Integrations Dialog */}
      <IntegrationsDialog 
        open={integrationsDialogOpen} 
        onOpenChange={setIntegrationsDialogOpen} 
      />
    </div>
  );
};

export default DataHub;
